import React, { useEffect } from "react";
import {
  joinClasses,
  sectionTagClassName,
} from "./shared.js";
import { pageLoadingOverlayMetaItems } from "./pageLoadingOverlayMeta.js";

const ICON_CONTAINER_SIZE_CLASS_NAMES = {
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-11 w-11",
};

export function IconContainer({
  children = null,
  className = "",
  size = "md",
  ...props
}) {
  return (
    <span
      className={joinClasses([
        "flex shrink-0 items-center justify-center rounded-panel border border-border-icon-shell bg-surface-icon-shell shadow-icon-inset",
        ICON_CONTAINER_SIZE_CLASS_NAMES[size] || ICON_CONTAINER_SIZE_CLASS_NAMES.md,
        className,
      ])}
      {...props}
    >
      {children}
    </span>
  );
}

export function CardShell({
  as: Component = "div",
  children = null,
  className = "",
  ...props
}) {
  return (
    <Component
      className={joinClasses([
        "rounded-[8px] border border-[rgba(148,163,184,0.16)] bg-white shadow-[0_8px_18px_rgba(15,23,42,0.035)]",
        className,
      ])}
      {...props}
    >
      {children}
    </Component>
  );
}

export function PanelShell({
  actions = null,
  actionsClassName = "",
  bodyClassName = "",
  children = null,
  className = "",
  copyClassName = "",
  description = "",
  descriptionClassName = "",
  descriptionProps = {},
  eyebrow = "",
  eyebrowClassName = "",
  eyebrowProps = {},
  headerClassName = "",
  title = "",
  titleClassName = "",
  titleProps = {},
  ...props
}) {
  const hasHeader = Boolean(eyebrow || title || description || actions);

  return (
    <div className={joinClasses(["grid gap-4", className])} {...props}>
      {hasHeader ? (
        <div className={joinClasses([
          "flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between",
          headerClassName,
        ])}>
          <div className={joinClasses(["grid max-w-[72ch] gap-1.5", copyClassName])}>
            {eyebrow ? (
              <p
                className={joinClasses([
                  sectionTagClassName,
                  eyebrowClassName,
                ])}
                {...eyebrowProps}
              >
                {eyebrow}
              </p>
            ) : null}
            {title ? (
              <h2
                className={joinClasses([
                  "font-display text-[clamp(1.18rem,1.65vw,1.7rem)] font-semibold leading-[1.06] tracking-[-0.03em] text-text",
                  titleClassName,
                ])}
                {...titleProps}
              >
                {title}
              </h2>
            ) : null}
            {description ? (
              <p
                className={joinClasses([
                  "text-sm leading-7 text-muted lg:text-[0.94rem]",
                  descriptionClassName,
                ])}
                {...descriptionProps}
              >
                {description}
              </p>
            ) : null}
          </div>
          {actions ? (
            <div className={joinClasses([
              "flex flex-wrap gap-2 lg:justify-end",
              actionsClassName,
            ])}>
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      {children ? <div className={bodyClassName}>{children}</div> : null}
    </div>
  );
}

export function WorkspaceShell({ children = null, className = "", ...props }) {
  return (
    <div
      className={joinClasses(["relative isolate", className])}
      data-results-workspace-shell="true"
      {...props}
    >
      {children}
    </div>
  );
}

export function PageLoadingOverlay({
  eyebrow = "Optimisation flotte",
  isOpen = false,
  message = "Nous préparons les affectations, la carte de trajet et les indicateurs de flotte.",
  metaItems = pageLoadingOverlayMetaItems,
  title = "Chargement de la page",
}) {
  const resolvedMetaItems = Array.isArray(metaItems) && metaItems.length
    ? metaItems
    : pageLoadingOverlayMetaItems;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      aria-hidden="false"
      className="fixed inset-0 z-overlay grid place-items-center bg-slate-950/20 p-6 animate-page-loading-overlay"
      data-react-page-loading-overlay="true"
    >
      <section
        aria-atomic="true"
        aria-live="polite"
        className="relative grid w-full max-w-115 gap-5 overflow-hidden rounded-overlay border border-line-strong bg-white p-7 shadow-floating animate-page-loading-dialog"
        role="status"
      >
        <div className="relative grid gap-2">
          <p className={sectionTagClassName}>{eyebrow}</p>
          <h2 className="font-display text-[clamp(2rem,4vw,2.6rem)] font-semibold leading-none tracking-[-0.04em] text-text">
            {title}
          </h2>
          <p className="max-w-[34ch] text-[0.98rem] leading-7 text-muted-strong">
            {message}
          </p>
        </div>

        <div className="relative flex items-center gap-4 rounded-xl border border-surface-accent-tint bg-slate-50 px-4 py-4 shadow-inset-highlight">
          <span className="h-13.5 w-13.5 shrink-0 rounded-full border-4 border-page-loading-ring border-r-page-loading-ring-strong border-t-accent shadow-page-loading-spinner animate-spin" />
          <div aria-hidden="true" className="inline-flex min-h-3.5 items-center gap-2">
            {resolvedMetaItems.slice(0, 3).map((item, index) => (
              <span
                key={`loading-dot-${item.key || index}`}
                className="h-2.5 w-2.5 rounded-full bg-accent opacity-40 shadow-page-loading-dot animate-page-loading-dot"
                style={{ animationDelay: `${index * 120}ms` }}
              />
            ))}
          </div>
        </div>

        <div className="relative grid gap-2.5 sm:grid-cols-3">
          {resolvedMetaItems.map((item) => (
            <div
              key={item.key || `${item.label}-${item.value}`}
              className="grid gap-1.5 rounded-lg border border-surface-accent-tint-soft bg-white px-3.5 py-3 shadow-page-loading-meta"
            >
              <span className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-accent">
                {item.label}
              </span>
              <strong className="text-[0.92rem] font-black leading-[1.35] text-text">
                {item.value}
              </strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
